import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  SquareArtDayListResponseSchema,
  SquareArtDaySchema,
  SquareCatalogResponseSchema,
  SquareConnectResponseSchema,
  SquareStatusSchema,
  type SquareArtDay,
  type SquareArtDayListResponse,
  type SquareCatalogResponse,
  type SquareMapArtDayInput,
  type SquareStatus,
} from "@/api/types/square";

/** Query-key factory — same shape as reviewKeys / the other hooks. */
export const squareKeys = {
  all: ["square"] as const,
  status: () => [...squareKeys.all, "status"] as const,
  catalog: () => [...squareKeys.all, "catalog"] as const,
  artDays: () => [...squareKeys.all, "art-days"] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: squareKeys.all });
}

/** GET /v2/square/status — configured / connected state for the page's status card. */
export function useSquareStatus() {
  return useQuery<SquareStatus>({
    queryKey: squareKeys.status(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/square/status");
      return validateResponse(SquareStatusSchema, data, "GET /v2/square/status");
    },
    staleTime: 30 * 1000,
  });
}

/**
 * GET /v2/square/catalog — 409 when not connected, so callers pass
 * `enabled = connected` and the query stays idle until then.
 */
export function useSquareCatalog(enabled: boolean) {
  return useQuery<SquareCatalogResponse>({
    queryKey: squareKeys.catalog(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/square/catalog");
      return validateResponse(SquareCatalogResponseSchema, data, "GET /v2/square/catalog");
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

/** GET /v2/square/art-days — only mounted by the page while connected. */
export function useSquareArtDays() {
  return useQuery<SquareArtDayListResponse>({
    queryKey: squareKeys.artDays(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/square/art-days");
      return validateResponse(SquareArtDayListResponseSchema, data, "GET /v2/square/art-days");
    },
    staleTime: 15 * 1000,
  });
}

/**
 * GET /v2/square/oauth/connect. Resolves to the Square consent URL; the caller
 * sends the browser there with `window.location.assign`.
 */
export function useSquareConnect() {
  const queryClient = useQueryClient();
  return useMutation<string, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.get("/v2/square/oauth/connect");
      return validateResponse(
        SquareConnectResponseSchema,
        data,
        "GET /v2/square/oauth/connect",
      ).authorize_url;
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

/** POST /v2/square/disconnect — returns the post-disconnect status. */
export function useSquareDisconnect() {
  const queryClient = useQueryClient();
  return useMutation<SquareStatus, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.post("/v2/square/disconnect");
      return validateResponse(SquareStatusSchema, data, "POST /v2/square/disconnect");
    },
    onSuccess: (status) => {
      queryClient.setQueryData(squareKeys.status(), status);
      invalidateAll(queryClient);
    },
  });
}

/**
 * PUT /v2/square/art-days/{id}. `variation_id: null` turns online checkout off
 * for that date. 422 when the variation isn't in the merchant's catalog.
 */
export function useMapArtDay() {
  const queryClient = useQueryClient();
  return useMutation<SquareArtDay, unknown, SquareMapArtDayInput>({
    mutationFn: async ({ id, variation_id }) => {
      const { data } = await apiClient.put(`/v2/square/art-days/${id}`, { variation_id });
      return validateResponse(SquareArtDaySchema, data, "PUT /v2/square/art-days/:id");
    },
    onSuccess: (updated) => {
      // Swap the row in place so the Select doesn't snap back while the list refetches.
      queryClient.setQueryData<SquareArtDayListResponse>(squareKeys.artDays(), (old) =>
        old && {
          items: old.items.map((item) => (item.id === updated.id ? updated : item)),
        },
      );
      invalidateAll(queryClient);
    },
  });
}
